import CircleIcon from '@mui/icons-material/Circle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface Props {
  disabled: boolean;
  /**
   * True when the button is disabled purely because every licence seat is taken. The
   * button gave no reason at all before, so hitting the limit looked like a malfunction —
   * it was reported as a bug ("Irgendwie kann ich keine weiteren Accounts hinzufügen.
   * Warum"). A disabled control has to say why.
   */
  disabledReasonNoSeats?: boolean;

  handleOpenAppSyncDialog(isStarterExperience: boolean): () => void;

  generateShareableCreationPath(): void;
}

const AddAccountButton: React.FC<Props> = ({
  disabled,
  disabledReasonNoSeats,
  handleOpenAppSyncDialog,
  generateShareableCreationPath,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const elementRef = useRef<any>(null);

  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = () => {
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  return (
    <>
      {/* A disabled MUI Button swallows pointer events, so the Tooltip needs a wrapper
          element of its own to hang the listeners on. */}
      <Tooltip title={disabled && disabledReasonNoSeats ? t('addAccountNoSeatsLeft') : ''}>
        <span>
          <Button ref={elementRef} className={classes.button} onClick={handleOpenMenu} disabled={disabled}>
            {t('addAccount')}
          </Button>
        </span>
      </Tooltip>

      <Menu
        classes={{ list: classes.menuList, paper: classes.menuPaper }}
        anchorEl={elementRef.current}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              width: 'auto',
            },
          },
        }}
        transitionDuration={0}
        disableEnforceFocus={true}
        disableAutoFocus={true}
        disableAutoFocusItem={true}
        disableRestoreFocus={true}
      >
        <MenuItem
          className={classes.headMenuItem}
          disableRipple={true}
          onClick={() => {
            handleCloseMenu();
          }}
        >
          <Stack direction={'row'} justifyContent={'space-between'} sx={{ width: '100%' }}>
            <Typography className={classes.buttonText}>{t('addAccount')}</Typography>
            <ExpandMoreIcon className={cx(classes.expandIcon, menuOpen ? 'expanded' : undefined)} />
          </Stack>
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          disableRipple={true}
          onClick={() => {
            handleOpenAppSyncDialog(false)();
            handleCloseMenu();
          }}
        >
          <Stack direction={'row'} gap={2} alignItems={'center'} sx={{ width: '100%' }}>
            <CircleIcon className={classes.dotIcon} />
            <Typography className={classes.menuItemText}>{t('addNewLinkedinAccount')}</Typography>
          </Stack>
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          disableRipple={true}
          onClick={() => {
            generateShareableCreationPath();
            handleCloseMenu();
          }}
        >
          <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
            <Stack direction={'row'} gap={2} alignItems={'center'} sx={{ width: '100%' }}>
              <CircleIcon className={classes.dotIcon} />
              <Typography className={classes.menuItemText}>{t('addAccountFromExternal')}</Typography>
            </Stack>
            <ContentCopyIcon className={classes.clipboardIcon} />
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  button: {
    height: 'fit-content',
    position: 'relative',
    borderRadius: '6px',
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    textTransform: 'none',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    color: '#FFF',
    lineHeight: 1,
    padding: '12px 48px',
    '&:hover': {
      background: 'linear-gradient(to right, #4AA4FF, #5044FF)',
    },
    '&.Mui-disabled': {
      background: theme.palette.action.disabledBackground,
    },
    '& svg': {
      fontSize: '20px !important',
    },
  },
  buttonText: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
  },
  expandIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.greys.medium,
    '&.expanded': {
      transform: 'rotate(180deg)',
    },
  },
  menuPaper: {
    borderRadius: '4px',
    maxHeight: '33vh',
    width: '300px !important',
  },
  menuList: {
    padding: 0,
  },
  headMenuItem: {
    fontSize: theme.typography.pxToRem(13),
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    padding: '8px 16px',
    '&:hover': {
      background: '#FFF',
    },
    '&:focus': {
      background: '#FFF',
    },
  },
  menuItem: {
    fontSize: theme.typography.pxToRem(13),
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    padding: '8px 16px',
    '&:last-of-type': {
      borderBottom: '1px solid #FFF',
    },
  },
  dotIcon: {
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.primary.main,
  },
  clipboardIcon: {
    fontSize: theme.typography.pxToRem(16),
    color: theme.palette.greys.medium,
  },
  menuItemText: {
    fontSize: theme.typography.pxToRem(13),
    '&.delete': {
      color: theme.palette.error.main,
    },
  },
}));

export default AddAccountButton;
